import React, { useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions, selectors } from "rdx";

import { styled } from "@mui/material/styles";

import { makeMap } from "lib/helpers/makeMap";

import ImpersonationBanner from "components/PlatformLayout/components/Banners/ImpersonationBanner";
import PastDueBanner from "./PastDueBanner";
import IntegrityViolationBanner from "./IntegrityViolationBanner";
import PendingSeatBanner from "./PendingSeatBanner";

const BANNER_TYPES = {
  PAST_DUE: "past_due",
  IMPERSONATING: "impersonating",
  CERTIFICATION: "certification",
  INCOMPLETE_VIOLATION: "incomplete_violation",
  PENDING_SEAT: "pending_seat",
} as const;

const Container = styled("div")({
  position: "fixed",
  zIndex: "1005",
  width: "100%",
});

const Banners = () => {
  const banners = useSelector(selectors.selectBanners);
  const elementRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const currentUser = useSelector(selectors.getCurrentUser);

  const updateHeight = useCallback(() => {
    const bannersHeight = elementRef.current?.clientHeight ?? 0;
    dispatch(actions.setBannersHeight(bannersHeight));
  }, [dispatch]);

  useEffect(() => {
    const element = elementRef.current;
    const observer = new ResizeObserver(updateHeight);

    if (element) {
      observer.observe(element);
      updateHeight();
    }

    return () => observer.disconnect();
  }, [updateHeight, currentUser?.enrollment?.paymentStatus]);

  const bannerReference = (banner) => {
    const list = makeMap({
      [BANNER_TYPES.PAST_DUE]: <PastDueBanner key={BANNER_TYPES.PAST_DUE} />,
      [BANNER_TYPES.IMPERSONATING]: <ImpersonationBanner key={BANNER_TYPES.IMPERSONATING} />,
      [BANNER_TYPES.CERTIFICATION]: null,
      [BANNER_TYPES.INCOMPLETE_VIOLATION]: (
        <IntegrityViolationBanner bannerProps={banner.props || {}} key={BANNER_TYPES.INCOMPLETE_VIOLATION} />
      ),
      [BANNER_TYPES.PENDING_SEAT]: (
        <PendingSeatBanner bannerProps={banner.props || {}} key={BANNER_TYPES.PENDING_SEAT} />
      ),
    });

    return list.get(banner.label);
  };

  return (
    <Container ref={elementRef}>
      {banners?.map((banner) => {
        return bannerReference(banner);
      })}
    </Container>
  );
};

export default Banners;
